import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, copyText, API } from "@/lib/api";
import { toast } from "sonner";
import { ShareNetwork, LinkSimple, TwitterLogo, FacebookLogo, WhatsappLogo, ArrowLeft, Trophy } from "@phosphor-icons/react";
import { BoxScoreView } from "@/components/BoxScoreView";

const LEAGUE = "IEFSC Basketball League";

export default function GameRecap() {
  const { gameId } = useParams();
  const [box, setBox] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/games/${gameId}/boxscore`).then((r) => setBox(r.data)).catch(() => setError(true));
  }, [gameId]);

  useEffect(() => {
    if (box) document.title = `${box.home_team.name} ${box.home_score}–${box.away_score} ${box.away_team.name} · ${LEAGUE}`;
    return () => { document.title = LEAGUE; };
  }, [box]);

  const url = `${API}/recap/${gameId}`;
  const shareText = box
    ? `${box.home_team.name} ${box.home_score}–${box.away_score} ${box.away_team.name} · Final${box.player_of_game_name ? ` — Player of the Game: ${box.player_of_game_name}` : ""} | ${LEAGUE}`
    : LEAGUE;

  const copyLink = async () => {
    if (await copyText(url)) toast.success("Link copied!");
    else toast.error("Could not copy link");
  };
  const openShare = (href) => window.open(href, "_blank", "noopener,width=600,height=520");

  const ShareBtn = ({ onClick, icon: Icon, label, testid, color }) => (
    <button onClick={onClick} data-testid={testid} className="flex items-center gap-2 border border-white/15 hover:border-[var(--c)] hover:text-[var(--c)] text-white/75 text-sm font-bold uppercase tracking-wide px-4 py-2.5 transition-all duration-200" style={{ "--c": color }}>
      <Icon size={18} weight="bold" /> {label}
    </button>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="game-recap-page">
      <Link to="/schedule" className="inline-flex items-center gap-1.5 text-white/50 hover:text-[#FF5722] text-sm uppercase tracking-wide mb-6 transition-colors">
        <ArrowLeft size={16} /> Back to Schedule
      </Link>

      {error && <div className="text-center py-24 text-white/40" data-testid="recap-error">This game recap could not be found.</div>}
      {!error && !box && <div className="text-center py-24 text-white/40">Loading recap…</div>}

      {box && (
        <>
          <div className="mb-8">
            <p className="text-xs uppercase tracking-[0.25em] text-[#FF5722] font-semibold mb-1 flex items-center gap-2"><Trophy size={14} weight="fill" /> Game Recap</p>
            <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none">Final Box Score</h1>
          </div>

          <div className="border border-white/10 bg-[#121212] p-5 sm:p-7 mb-6">
            <BoxScoreView box={box} />
          </div>

          <div className="border border-white/10 bg-[#121212] p-5" data-testid="recap-share-bar">
            <p className="text-xs uppercase tracking-[0.2em] text-white/50 font-semibold mb-3 flex items-center gap-2"><ShareNetwork size={16} className="text-[#FF5722]" /> Share this recap</p>
            <div className="flex flex-wrap gap-2.5">
              <ShareBtn onClick={copyLink} icon={LinkSimple} label="Copy Link" testid="recap-copy-link" color="#FF5722" />
              <ShareBtn onClick={() => openShare(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`)} icon={TwitterLogo} label="X" testid="recap-share-x" color="#1DA1F2" />
              <ShareBtn onClick={() => openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)} icon={FacebookLogo} label="Facebook" testid="recap-share-fb" color="#1877F2" />
              <ShareBtn onClick={() => openShare(`https://wa.me/?text=${encodeURIComponent(shareText + " " + url)}`)} icon={WhatsappLogo} label="WhatsApp" testid="recap-share-wa" color="#25D366" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
